import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMethod } from '../../../lib/hooks';
import type { ComparisonItem } from '../components/Topbar';
import type {
  CapacityBasis,
  ConnectedSourceId,
  DemandIntelPayload,
  DrillDownData,
  FleetRestaurant,
  ForecastData,
  GuestReviewsPayload,
  HeroNugget,
  NorthStarDrillDownData,
  NorthStarPayload,
  OpsCommandLivePayload,
  OpsCommandPerformancePayload,
  OpsCommandScopePayload,
  OpsScopeConfigPayload,
  PerfSettings,
  RecoveryData,
  ReservationSourceId,
  ReservationStatusGroup,
  ScopeGroupConfig,
  ScopeGroupSummary,
  SettingsAuditPayload,
  SubscriptionUsagePayload,
  ShiftCatalogItem,
  ShiftFilter,
  StatusFilter,
} from '../types';
import { DEFAULT_COMPARE_KIND, effectiveCompareKind, localizeComparePeriod } from '../lib/perfSettings';
import {
  ALWAYS_ON_SOURCES,
  OPT_OUT_BY_DEFAULT_SOURCES,
} from '../data/sources';
import { HORIZON_ENABLED, LIVE_SERVICE_ENABLED } from '../lib/opsFlags';
import { mergeSettingsAudits } from '../lib/mergeSettingsAudits';
import type {
  ScopeAttributeFamily,
  ScopeAttributesPayload,
} from '../lib/scopeGroupFilters';
import type {
  ScopePeriodMetricsPayload,
  ScopePeriodMetricsRequest,
} from '../lib/scopeKpi';
import {
  scopeGroupIdFromFilter,
  scopeMembershipFingerprint,
} from '../lib/scopeFilter';

/** Cache-only SWR key segment — busts stale `group:current-selection` responses. */
function scopeKeyExtra(regionFilter: string | null | undefined) {
  return scopeMembershipFingerprint(regionFilter) ?? undefined;
}

const MODE_KEY = 'aleno_ops_mode';
const DISCONNECTED_SOURCES_KEY = 'aleno_ops_disconnected_sources';

export type OpsScopeOverlayContext = {
  scopeGroups: ScopeGroupSummary[];
  scopeGroupConfigs: ScopeGroupConfig[];
  fleetRestaurants: FleetRestaurant[];
  connected: ConnectedSourceId[];
};

function defaultDisconnectedSources(): Set<ConnectedSourceId> {
  return new Set(OPT_OUT_BY_DEFAULT_SOURCES);
}

function readDisconnectedSources(): Set<ConnectedSourceId> {
  if (typeof window === 'undefined') {
    return defaultDisconnectedSources();
  }
  try {
    const raw = localStorage.getItem(DISCONNECTED_SOURCES_KEY);
    if (raw === null) {
      return defaultDisconnectedSources();
    }
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return defaultDisconnectedSources();
    }
    return new Set(
      parsed.filter(
        (id): id is ConnectedSourceId =>
          typeof id === 'string' &&
          !ALWAYS_ON_SOURCES.includes(id as ConnectedSourceId),
      ),
    );
  } catch {
    return defaultDisconnectedSources();
  }
}

function persistDisconnectedSources(next: Set<ConnectedSourceId>) {
  if (typeof window === 'undefined') {
    return;
  }
  const cleaned = [...next].filter(id => !ALWAYS_ON_SOURCES.includes(id));
  localStorage.setItem(DISCONNECTED_SOURCES_KEY, JSON.stringify(cleaned));
}

// Multi-select reservation-source lens: a list of canonical source ids
// (empty = all sources, no filter).
export type SourceFilter = ReservationSourceId[];

// Normalize the UI source selection to the wire argument the methods expect:
// null = no filter (all sources), otherwise the list of selected source ids.
function perfSettingsTailArgs(settings: PerfSettings) {
  return [
    settings.dateStart ?? null,
    settings.dateEnd ?? null,
    settings.capacityBasis ?? null,
    settings.compareKind ?? null,
    settings.compareDateStart ?? null,
    settings.compareDateEnd ?? null,
  ] as const;
}

function buildPerfMethodArgs(
  restaurantId: string,
  settings: PerfSettings,
  scope: string | null,
  source: SourceFilter | null,
  status: StatusFilter | null,
  shifts: ShiftFilter | null = null,
) {
  return [
    restaurantId,
    settings.aggregation,
    settings.frameCount,
    settings.frameUnit,
    scope,
    sourceArg(source),
    statusArg(status),
    ...perfSettingsTailArgs(settings),
    shiftArg(shifts),
  ] as const;
}

function sourceArg(source: SourceFilter | null): ReservationSourceId[] | null {
  return source && source.length ? source : null;
}

// Normalize the UI status selection to the wire argument: null = no filter
// (all statuses), otherwise the list of selected status-group ids.
function statusArg(status: StatusFilter | null): ReservationStatusGroup[] | null {
  return status && status.length ? status : null;
}

function shiftArg(shifts: ShiftFilter | null): string[] | null {
  return shifts && shifts.length ? shifts : null;
}

function capacityArg(basis: CapacityBasis | null | undefined): CapacityBasis | null {
  return basis && basis !== 'seats' ? basis : null;
}

function liveMethodArgs(
  restaurantId: string,
  scopeArg: string | null,
  source: SourceFilter | null,
  status: StatusFilter | null,
  capacityBasis: CapacityBasis | null | undefined,
  shifts: ShiftFilter | null = null,
): [
  string,
  string | null,
  null,
  ReservationSourceId[] | null,
  ReservationStatusGroup[] | null,
  CapacityBasis | null,
  string[] | null,
] {
  // Positional nowMs stays null — Live always uses wall-clock "now".
  return [
    restaurantId,
    scopeArg,
    null,
    sourceArg(source),
    statusArg(status),
    capacityArg(capacityBasis),
    shiftArg(shifts),
  ];
}

export type OpsMode =
  | 'live'
  | 'performance'
  | 'settings'
  | 'growth'
  | 'horizon'
  | 'hotel'
  | 'assistant';

function coerceOpsMode(mode: OpsMode): OpsMode {
  if (!LIVE_SERVICE_ENABLED && mode === 'live') return 'performance';
  if (!HORIZON_ENABLED && mode === 'horizon') return 'performance';
  return mode;
}

export function useOpsMode(initial?: OpsMode) {
  const [mode, setModeState] = useState<OpsMode>(() => {
    // URL/share bootstrap wins; otherwise Performance is the primary landing
    // for the embedded agent (not last-used Live).
    if (initial !== undefined) return coerceOpsMode(initial);
    return coerceOpsMode('performance');
  });

  const setMode = useCallback((m: OpsMode) => {
    const next = coerceOpsMode(m);
    setModeState(next);
    localStorage.setItem(MODE_KEY, next);
  }, []);

  /** Tour/help navigation — updates UI without rewriting preferred mode. */
  const setModeTransient = useCallback((m: OpsMode) => {
    setModeState(coerceOpsMode(m));
  }, []);

  return [mode, setMode, setModeTransient] as const;
}

export function pruneShiftsToCatalog(
  selected: ShiftFilter,
  catalog: ShiftCatalogItem[] | undefined,
): ShiftFilter {
  if (!catalog?.length || !selected.length) return selected;
  const allowed = new Set(catalog.map(row => row.name.toLowerCase()));
  return selected.filter(name => allowed.has(name.toLowerCase()));
}

// The view context (venues, period, comparison, status, source and shift
// filters) lives in hooks/useViewContext.ts, kept per person and org; the
// former global keys aleno_ops_source / _status / _shifts / _perf_settings
// are migrated there once (lib/viewContext.ts).

/**
 * Optional data sources the server offers can be hidden via the integrations
 * drawer without changing backend configuration. Persisted per browser.
 */
export function useOpsConnectedPreferences() {
  const [disconnected, setDisconnected] = useState<Set<ConnectedSourceId>>(
    readDisconnectedSources,
  );

  const toggle = useCallback((id: ConnectedSourceId) => {
    if (ALWAYS_ON_SOURCES.includes(id)) {
      return;
    }
    setDisconnected(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      persistDisconnectedSources(next);
      return next;
    });
  }, []);

  const apply = useCallback(
    (serverConnected: ConnectedSourceId[]) =>
      serverConnected.filter(
        id => ALWAYS_ON_SOURCES.includes(id) || !disconnected.has(id),
      ),
    [disconnected],
  );

  return { disconnected, toggle, apply };
}

export function useOpsCommandLive(
  restaurantId: string | undefined,
  regionFilter: string | null,
  source: SourceFilter | null = null,
  status: StatusFilter | null = null,
  capacityBasis: CapacityBasis | null = null,
  enabled = true,
  shifts: ShiftFilter | null = null,
) {
  const scopeArg =
    !regionFilter || regionFilter === 'all' ? null : regionFilter;

  return useMethod<OpsCommandLivePayload>(
    'opsCommand/getLive',
    restaurantId && enabled
      ? liveMethodArgs(
          restaurantId,
          scopeArg,
          source,
          status,
          capacityBasis,
          shifts,
        )
      : null,
    // Poll only while this hook has a key (Live tab / live compare). Keep the
    // previous board painted while a refresh is in flight — never blank the UI.
    {
      refreshInterval: 60_000,
      dedupingInterval: 5_000,
      keepPreviousData: true,
      keyExtra: scopeKeyExtra(regionFilter),
    },
  );
}

/** Full-fleet scope catalog for the region/group picker (always unscoped). */
export function useOpsScopeCatalog(restaurantId: string | undefined) {
  return useMethod<OpsCommandScopePayload>(
    'opsCommand/getScope',
    restaurantId ? [restaurantId, null, null] : null,
    // No interval — scope metadata is not live. Refetch via mutate() after
    // group edits. A 60s poll here was refreshing Performance/Settings too.
    { keepPreviousData: true },
  );
}

/** Scoped outlet count for the topbar label (respects the active region/group filter). */
export function useOpsScopedOutletCount(
  restaurantId: string | undefined,
  regionFilter: string | null,
) {
  const scopeArg =
    !regionFilter || regionFilter === 'all' ? null : regionFilter;

  return useMethod<OpsCommandScopePayload>(
    'opsCommand/getScope',
    restaurantId ? [restaurantId, scopeArg, null] : null,
    { keepPreviousData: true, keyExtra: scopeKeyExtra(regionFilter) },
  );
}

export function useOpsForecast(
  restaurantId: string | undefined,
  regionFilter: string | null,
  enabled: boolean,
  source: SourceFilter | null = null,
  status: StatusFilter | null = null,
  capacityBasis: CapacityBasis | null = null,
  shifts: ShiftFilter | null = null,
) {
  const scopeArg =
    !regionFilter || regionFilter === 'all' ? null : regionFilter;

  return useMethod<ForecastData>(
    'opsCommand/getForecast',
    restaurantId && enabled
      ? liveMethodArgs(
          restaurantId,
          scopeArg,
          source,
          status,
          capacityBasis,
          shifts,
        )
      : null,
    // Fallback only — Live payload already carries forecast and polls at 60s.
    { keepPreviousData: true, keyExtra: scopeKeyExtra(regionFilter) },
  );
}

export function useOpsRecovery(
  restaurantId: string | undefined,
  regionFilter: string | null,
  enabled: boolean,
  source: SourceFilter | null = null,
  status: StatusFilter | null = null,
  capacityBasis: CapacityBasis | null = null,
  shifts: ShiftFilter | null = null,
) {
  const scopeArg =
    !regionFilter || regionFilter === 'all' ? null : regionFilter;

  return useMethod<RecoveryData>(
    'opsCommand/getRecovery',
    restaurantId && enabled
      ? liveMethodArgs(
          restaurantId,
          scopeArg,
          source,
          status,
          capacityBasis,
          shifts,
        )
      : null,
    // Fallback only — Live payload already carries recovery and polls at 60s.
    { keepPreviousData: true, keyExtra: scopeKeyExtra(regionFilter) },
  );
}

export function useOpsRegime(
  restaurantId: string | undefined,
  regionFilter: string | null,
  enabled: boolean,
  source: SourceFilter | null = null,
) {
  return useMethod<{
    cells: { day: number; shift: string; value: number; vsTypical: number }[];
    liveValue: number;
    liveDay: number;
    liveShift: string;
  }>(
    'opsCommand/getRegime',
    restaurantId && enabled
      ? [restaurantId, regionFilter, null, sourceArg(source)]
      : null,
    { refreshInterval: 300_000, keyExtra: scopeKeyExtra(regionFilter) },
  );
}

/**
 * The method result with its comparison window named in the person's
 * language (localizeComparePeriod); every other field reads through
 * untouched, so SWR keeps tracking only what the screen reads.
 */
function useLocalizedCompare<R extends { data?: unknown }>(result: R): R {
  const { i18n } = useTranslation();
  const data = result.data as Parameters<typeof localizeComparePeriod>[0];
  const localized = useMemo(() => localizeComparePeriod(data), [data, i18n.language]);
  return useMemo(
    () =>
      localized === data
        ? result
        : (new Proxy(result, { get: (target, key) => (key === 'data' ? localized : Reflect.get(target, key)) }) as R),
    [result, localized, data],
  );
}

export function useOpsPerformance(
  restaurantId: string | undefined,
  settings: PerfSettings,
  regionFilter: string | null,
  enabled: boolean,
  source: SourceFilter | null = null,
  status: StatusFilter | null = null,
  shifts: ShiftFilter | null = null,
) {
  const scopeArg =
    !regionFilter || regionFilter === 'all' ? null : regionFilter;

  // OpenTable-tagged scopes are resolved server-side (the handler swaps in the
  // bundled monthly aggregates joined to the live aleno seats/region), so the
  // client just consumes the payload like any other scope.
  return useLocalizedCompare(
    useMethod<OpsCommandPerformancePayload>(
      'opsCommand/getPerformance',
      restaurantId && enabled
        ? buildPerfMethodArgs(restaurantId, settings, scopeArg, source, status, shifts)
        : null,
      { keepPreviousData: true, keyExtra: scopeKeyExtra(regionFilter) },
    ),
  );
}

/** The scope's events (shifts flagged as events) for the Performance period. */
export function useOpsEvents(
  restaurantId: string | undefined,
  settings: PerfSettings,
  regionFilter: string | null,
  enabled: boolean,
  source: SourceFilter | null = null,
  status: StatusFilter | null = null,
) {
  const scopeArg =
    !regionFilter || regionFilter === 'all' ? null : regionFilter;
  return useLocalizedCompare(
    useMethod<import('../types').EventsOverview>(
      'opsCommand/getEvents',
      restaurantId && enabled
        ? buildPerfMethodArgs(restaurantId, settings, scopeArg, source, status, null)
        : null,
      { keepPreviousData: true, keyExtra: scopeKeyExtra(regionFilter) },
    ),
  );
}

/** Segment hero nuggets — heavy driver-row scan, loaded after the scorecard. */
export function useOpsPerformanceHeroNuggets(
  restaurantId: string | undefined,
  settings: PerfSettings,
  regionFilter: string | null,
  enabled: boolean,
  source: SourceFilter | null = null,
  status: StatusFilter | null = null,
  shifts: ShiftFilter | null = null,
) {
  const scopeArg =
    !regionFilter || regionFilter === 'all' ? null : regionFilter;

  return useMethod<{ heroNuggets: HeroNugget[] }>(
    'opsCommand/getPerformanceHeroNuggets',
    restaurantId && enabled
      ? buildPerfMethodArgs(restaurantId, settings, scopeArg, source, status, shifts)
      : null,
    { keepPreviousData: true, keyExtra: scopeKeyExtra(regionFilter) },
  );
}

/** Mode-scoped embedded-agent brief (insight → why → action → evidence). */
export function useOpsSituationPack(
  restaurantId: string | undefined,
  mode: OpsMode,
  settings: PerfSettings,
  regionFilter: string | null,
  enabled: boolean,
  source: SourceFilter | null = null,
  status: StatusFilter | null = null,
  shifts: ShiftFilter | null = null,
) {
  const scopeArg =
    !regionFilter || regionFilter === 'all' ? null : regionFilter;

  return useMethod<import('../lib/situationTypes').SituationPackPayload>(
    'opsCommand/getSituationPack',
    restaurantId && enabled
      ? [
          restaurantId,
          mode,
          scopeArg,
          sourceArg(source),
          statusArg(status),
          settings.aggregation,
          settings.frameCount,
          settings.frameUnit,
          settings.dateStart ?? null,
          settings.dateEnd ?? null,
          settings.capacityBasis ?? null,
          settings.compareKind ?? null,
          settings.compareDateStart ?? null,
          settings.compareDateEnd ?? null,
          null,
          shiftArg(shifts),
        ]
      : null,
    {
      keepPreviousData: true,
      keyExtra: `${scopeKeyExtra(regionFilter)}|sit:${mode}`,
      refreshInterval: mode === 'live' ? 60_000 : 180_000,
    },
  );
}

/** Async driver diagnosis — enriches the light Performance situation pack. */
export function useOpsSituationDiagnosis(
  restaurantId: string | undefined,
  settings: PerfSettings,
  regionFilter: string | null,
  enabled: boolean,
  source: SourceFilter | null = null,
  status: StatusFilter | null = null,
  shifts: ShiftFilter | null = null,
) {
  const scopeArg =
    !regionFilter || regionFilter === 'all' ? null : regionFilter;

  return useMethod<import('../lib/situationTypes').SituationDiagnosisPayload>(
    'opsCommand/getSituationDiagnosis',
    restaurantId && enabled
      ? [
          restaurantId,
          scopeArg,
          sourceArg(source),
          statusArg(status),
          settings.aggregation,
          settings.frameCount,
          settings.frameUnit,
          settings.dateStart ?? null,
          settings.dateEnd ?? null,
          settings.capacityBasis ?? null,
          settings.compareKind ?? null,
          settings.compareDateStart ?? null,
          settings.compareDateEnd ?? null,
          shiftArg(shifts),
        ]
      : null,
    {
      keepPreviousData: false,
      keyExtra: `${scopeKeyExtra(regionFilter)}|sitdiag`,
      refreshInterval: 180_000,
    },
  );
}

export function useOpsSettingsAudit(
  restaurantId: string | undefined,
  settings: PerfSettings,
  regionFilter: string | null,
  enabled: boolean,
) {
  const scopeArg =
    !regionFilter || regionFilter === 'all' ? null : regionFilter;

  return useMethod<SettingsAuditPayload>(
    'opsCommand/getSettingsAudit',
    restaurantId && enabled
      ? [
          restaurantId,
          scopeArg,
          settings.aggregation,
          settings.frameCount,
          settings.frameUnit,
          settings.dateStart ?? null,
          settings.dateEnd ?? null,
        ]
      : null,
    { refreshInterval: 300_000, keyExtra: scopeKeyExtra(regionFilter) },
  );
}

export function useOpsSubscriptionUsage(
  restaurantId: string | undefined,
  regionFilter: string | null,
  enabled: boolean,
) {
  const scopeArg =
    !regionFilter || regionFilter === 'all' ? null : regionFilter;

  return useMethod<SubscriptionUsagePayload>(
    'opsCommand/getSubscriptionUsage',
    restaurantId && enabled ? [restaurantId, scopeArg] : null,
    { keyExtra: scopeKeyExtra(regionFilter) },
  );
}

export function useOpsDemandIntel(
  restaurantId: string | undefined,
  settings: PerfSettings,
  regionFilter: string | null,
  enabled: boolean,
) {
  const scopeArg =
    !regionFilter || regionFilter === 'all' ? null : regionFilter;

  return useMethod<DemandIntelPayload>(
    'opsCommand/getDemandIntel',
    restaurantId && enabled
      ? [
          restaurantId,
          settings.aggregation,
          settings.frameCount,
          settings.frameUnit,
          scopeArg,
        ]
      : null,
    {
      refreshInterval: 300_000,
      keepPreviousData: true,
      keyExtra: scopeKeyExtra(regionFilter),
    },
  );
}

export function useOpsGuestReviews(
  restaurantId: string | undefined,
  settings: PerfSettings,
  regionFilter: string | null,
  enabled: boolean,
) {
  const scopeArg =
    !regionFilter || regionFilter === 'all' ? null : regionFilter;

  return useMethod<GuestReviewsPayload>(
    'opsCommand/getGuestReviews',
    restaurantId && enabled
      ? [
          restaurantId,
          settings.aggregation,
          settings.frameCount,
          settings.frameUnit,
          scopeArg,
          settings.dateStart ?? null,
          settings.dateEnd ?? null,
          settings.capacityBasis ?? null,
        ]
      : null,
    {
      refreshInterval: 300_000,
      keepPreviousData: true,
      keyExtra: scopeKeyExtra(regionFilter),
    },
  );
}

export function useOpsDrillDown(
  restaurantId: string | undefined,
  metricKey: string | null,
  regionFilter: string | null,
  source: SourceFilter | null = null,
  status: StatusFilter | null = null,
  capacityBasis: CapacityBasis | null = null,
  shifts: ShiftFilter | null = null,
) {
  const scopeArg =
    !regionFilter || regionFilter === 'all' ? null : regionFilter;

  return useMethod<DrillDownData>(
    'opsCommand/getDrillDown',
    restaurantId && metricKey
      ? [
          restaurantId,
          metricKey,
          scopeArg,
          null,
          sourceArg(source),
          statusArg(status),
          capacityArg(capacityBasis),
          shiftArg(shifts),
        ]
      : null,
    { keepPreviousData: true, keyExtra: scopeKeyExtra(regionFilter) },
  );
}

export function perfDrillDataMatches(
  data: DrillDownData | undefined,
  metricKey: string,
): boolean {
  return !!data && data.key === metricKey;
}

/** True while drill-down data for the current request is not ready to render. */
export function perfDrillIsPending(drill: {
  isLoading: boolean;
  isValidating: boolean;
}): boolean {
  return drill.isLoading || drill.isValidating;
}

export function useOpsPerformanceDrillDown(
  restaurantId: string | undefined,
  metricKey: string | null,
  settings: PerfSettings,
  regionFilter: string | null,
  outletId: string | null = null,
  source: SourceFilter | null = null,
  status: StatusFilter | null = null,
  secondaryKey: string | null = null,
  shifts: ShiftFilter | null = null,
) {
  return useLocalizedCompare(useMethod<DrillDownData>(
    'opsCommand/getPerformanceDrillDown',
    restaurantId && metricKey
      ? [
          restaurantId,
          metricKey,
          settings.aggregation,
          settings.frameCount,
          settings.frameUnit,
          regionFilter,
          outletId,
          null,
          sourceArg(source),
          statusArg(status),
          settings.dateStart ?? null,
          settings.dateEnd ?? null,
          settings.capacityBasis ?? null,
          secondaryKey,
          settings.compareKind ?? null,
          settings.compareDateStart ?? null,
          settings.compareDateEnd ?? null,
          shiftArg(shifts),
        ]
      : null,
    { keepPreviousData: true, keyExtra: scopeKeyExtra(regionFilter) },
  ));
}

export type OpportunityNarrativePayload = {
  narrative: Extract<
    import('../types').DrillPanel,
    { kind: 'narrative' }
  > | null;
  /** Catalog finding first, then the cross-driver cohort; null = nothing found. */
  source: 'catalog' | 'cohort' | null;
  /** Echo of the request metric — use to ignore keepPreviousData leftovers. */
  metricKey: string;
};

/**
 * Opportunity card — fetched in parallel with the drill (does not block drill
 * paint). Redis-cached cross-user: insight catalog card, else the cohort
 * narrative from the drill's driver sample, else `narrative.empty`.
 */
export function useOpsOpportunityNarrative(
  restaurantId: string | undefined,
  metricKey: string | null,
  settings: PerfSettings,
  regionFilter: string | null,
  enabled: boolean,
  outletId: string | null = null,
  source: SourceFilter | null = null,
  status: StatusFilter | null = null,
  shifts: ShiftFilter | null = null,
) {
  const scopeArg =
    !regionFilter || regionFilter === 'all' ? null : regionFilter;

  return useMethod<OpportunityNarrativePayload>(
    'opsCommand/getOpportunityNarrative',
    restaurantId && metricKey && enabled
      ? [
          restaurantId,
          metricKey,
          settings.aggregation,
          settings.frameCount,
          settings.frameUnit,
          scopeArg,
          outletId,
          sourceArg(source),
          statusArg(status),
          settings.dateStart ?? null,
          settings.dateEnd ?? null,
          settings.capacityBasis ?? null,
          shiftArg(shifts),
        ]
      : null,
    { keepPreviousData: true, keyExtra: scopeKeyExtra(regionFilter) },
  );
}

export function useOpsNorthStar(
  restaurantId: string | undefined,
  settings: PerfSettings,
  regionFilter: string | null,
  enabled: boolean,
  source: SourceFilter | null = null,
  status: StatusFilter | null = null,
  shifts: ShiftFilter | null = null,
) {
  const scopeArg =
    !regionFilter || regionFilter === 'all' ? null : regionFilter;

  // getPerformance's args in its order: the hero reads the scorecard's
  // period and comparison.
  return useMethod<NorthStarPayload | null>(
    'opsCommand/getNorthStar',
    restaurantId && enabled
      ? buildPerfMethodArgs(restaurantId, settings, scopeArg, source, status, shifts)
      : null,
    {
      keepPreviousData: true,
      refreshInterval: 300_000,
      keyExtra: scopeKeyExtra(regionFilter),
    },
  );
}

export function useOpsNorthStarDrillDown(
  restaurantId: string | undefined,
  open: boolean,
  settings: PerfSettings,
  regionFilter: string | null,
  source: SourceFilter | null = null,
  status: StatusFilter | null = null,
  shifts: ShiftFilter | null = null,
) {
  const scopeArg =
    !regionFilter || regionFilter === 'all' ? null : regionFilter;

  // Same args as getNorthStar / getPerformance: one window for the hero,
  // components, weak points and the plan.
  return useMethod<NorthStarDrillDownData | null>(
    'opsCommand/getNorthStarDrillDown',
    restaurantId && open
      ? buildPerfMethodArgs(restaurantId, settings, scopeArg, source, status, shifts)
      : null,
    // A moved window loads its own numbers; it never shows the last window's.
    { keepPreviousData: false, keyExtra: scopeKeyExtra(regionFilter) },
  );
}

/** Fleet restaurant list for the single-restaurant picker. */
export function useOpsScopeConfig(restaurantId: string | undefined) {
  return useMethod<OpsScopeConfigPayload>(
    'opsCommand/getScopeConfig',
    restaurantId ? [restaurantId] : null,
    { refreshInterval: 300_000, keepPreviousData: true },
  );
}

/**
 * Every restaurant the signed-in user may open (all restaurants for a
 * superuser, the accessible set otherwise). Powers the global restaurant
 * switcher — independent of the currently selected fleet.
 */
export function useOpsAccessibleRestaurants(enabled = true) {
  return useMethod<{ restaurants: FleetRestaurant[] }>(
    'opsCommand/listAccessibleRestaurants',
    enabled ? [] : null,
    { refreshInterval: 600_000, keepPreviousData: true },
  );
}

/**
 * Settings / label attributes for the *entire* accessible set (Custom Groups
 * filter builder). Locale-independent codes; only fetched while a rule needs
 * them, so location-only filtering never waits on it.
 */
export function useOpsScopeAttributes(families: ScopeAttributeFamily[]) {
  return useMethod<ScopeAttributesPayload>(
    'opsCommand/getScopeAttributes',
    families.length ? [{ families }] : null,
    { revalidateOnFocus: false },
  );
}

/**
 * Per-restaurant period aggregates over the accessible set for one resolved
 * period (Custom Groups KPI filters). No keepPreviousData: a value from another
 * period must never be evaluated against the current rule.
 */
export function useOpsScopePeriodMetrics(request: ScopePeriodMetricsRequest | null) {
  return useMethod<ScopePeriodMetricsPayload>(
    'opsCommand/getScopePeriodMetrics',
    request ? [request] : null,
    { revalidateOnFocus: false },
  );
}

/** Parallel fetches for comparison mode (up to 5 items). Uses a fixed hook
 *  count so React's rules of hooks are satisfied regardless of item count. */
export function useOpsComparison({
  restaurantId,
  compareItems,
  mode,
  source,
  status,
  shifts = null,
  perfSettings,
  overlay,
  hasNorthStar = false,
}: {
  restaurantId: string;
  compareItems: ComparisonItem[];
  mode: 'live' | 'performance' | 'settings';
  source: SourceFilter | null;
  status: StatusFilter | null;
  shifts?: ShiftFilter | null;
  perfSettings: PerfSettings;
  overlay?: OpsScopeOverlayContext;
  /** When true, fetch North Star per compared scope in performance mode. */
  hasNorthStar?: boolean;
}) {
  const perfTailArgs = perfSettingsTailArgs(perfSettings);
  const enabled = compareItems.length >= 2;
  const items = compareItems.slice(0, 5);
  const fillerNull: null = null;

  function scopeArg(filter: string | null | undefined): string | null {
    return !filter || filter === 'all' ? null : filter;
  }

  function perfEnabled(index: number): boolean {
    const item = items[index];
    // OpenTable-tagged scopes resolve to the bundled aggregates server-side, so
    // they go through the same getPerformance call as every other scope.
    return !!(enabled && mode === 'performance' && item);
  }

  function northStarEnabled(index: number): boolean {
    return !!(hasNorthStar && perfEnabled(index));
  }

  function settingsEnabled(index: number): boolean {
    return !!(enabled && mode === 'settings' && items[index]);
  }

  function northStarArgs(filter: string) {
    return [
      restaurantId,
      perfSettings.aggregation,
      perfSettings.frameCount,
      perfSettings.frameUnit,
      scopeArg(filter),
      sourceArg(source),
      statusArg(status),
      ...perfTailArgs,
      shiftArg(shifts),
    ] as const;
  }

  function matchesPerfSettings(
    data: OpsCommandPerformancePayload | null | undefined,
  ): data is OpsCommandPerformancePayload {
    return (
      !!data &&
      data.aggregation === perfSettings.aggregation &&
      data.frameCount === perfSettings.frameCount &&
      data.frameUnit === perfSettings.frameUnit &&
      (data.dateRange?.start ?? null) === (perfSettings.dateStart ?? null) &&
      (data.dateRange?.end ?? null) === (perfSettings.dateEnd ?? null) &&
      (data.compareKind ?? data.comparePeriod?.kind ?? DEFAULT_COMPARE_KIND) ===
        effectiveCompareKind(perfSettings)
    );
  }

  // Fixed 5-slot hook calls for live data — poll only in live compare mode.
  const livePoll = (filter: string | null | undefined) =>
    ({
      refreshInterval: 60_000,
      dedupingInterval: 10_000,
      keepPreviousData: true,
      keyExtra: scopeKeyExtra(filter),
    }) as const;
  const live0 = useMethod<OpsCommandLivePayload>(
    'opsCommand/getLive',
    enabled && mode === 'live' && items[0]
      ? liveMethodArgs(
          restaurantId,
          scopeArg(items[0].filter),
          source,
          status,
          perfSettings.capacityBasis,
          shifts,
        )
      : fillerNull,
    livePoll(items[0]?.filter),
  );
  const live1 = useMethod<OpsCommandLivePayload>(
    'opsCommand/getLive',
    enabled && mode === 'live' && items[1]
      ? liveMethodArgs(
          restaurantId,
          scopeArg(items[1].filter),
          source,
          status,
          perfSettings.capacityBasis,
          shifts,
        )
      : fillerNull,
    livePoll(items[1]?.filter),
  );
  const live2 = useMethod<OpsCommandLivePayload>(
    'opsCommand/getLive',
    enabled && mode === 'live' && items[2]
      ? liveMethodArgs(
          restaurantId,
          scopeArg(items[2].filter),
          source,
          status,
          perfSettings.capacityBasis,
          shifts,
        )
      : fillerNull,
    livePoll(items[2]?.filter),
  );
  const live3 = useMethod<OpsCommandLivePayload>(
    'opsCommand/getLive',
    enabled && mode === 'live' && items[3]
      ? liveMethodArgs(
          restaurantId,
          scopeArg(items[3].filter),
          source,
          status,
          perfSettings.capacityBasis,
          shifts,
        )
      : fillerNull,
    livePoll(items[3]?.filter),
  );
  const live4 = useMethod<OpsCommandLivePayload>(
    'opsCommand/getLive',
    enabled && mode === 'live' && items[4]
      ? liveMethodArgs(
          restaurantId,
          scopeArg(items[4].filter),
          source,
          status,
          perfSettings.capacityBasis,
          shifts,
        )
      : fillerNull,
    livePoll(items[4]?.filter),
  );

  const perfHeadArgs = [
    perfSettings.aggregation,
    perfSettings.frameCount,
    perfSettings.frameUnit,
  ] as const;

  // Fixed 5-slot hook calls for performance data
  const perf0 = useLocalizedCompare(useMethod<OpsCommandPerformancePayload>(
    'opsCommand/getPerformance',
    perfEnabled(0)
      ? [
          restaurantId,
          ...perfHeadArgs,
          scopeArg(items[0]!.filter),
          sourceArg(source),
          statusArg(status),
          ...perfTailArgs,
          shiftArg(shifts),
        ]
      : fillerNull,
    { keepPreviousData: true, keyExtra: scopeKeyExtra(items[0]?.filter) },
  ));
  const perf1 = useLocalizedCompare(useMethod<OpsCommandPerformancePayload>(
    'opsCommand/getPerformance',
    perfEnabled(1)
      ? [
          restaurantId,
          ...perfHeadArgs,
          scopeArg(items[1]!.filter),
          sourceArg(source),
          statusArg(status),
          ...perfTailArgs,
          shiftArg(shifts),
        ]
      : fillerNull,
    { keepPreviousData: true, keyExtra: scopeKeyExtra(items[1]?.filter) },
  ));
  const perf2 = useLocalizedCompare(useMethod<OpsCommandPerformancePayload>(
    'opsCommand/getPerformance',
    perfEnabled(2)
      ? [
          restaurantId,
          ...perfHeadArgs,
          scopeArg(items[2]!.filter),
          sourceArg(source),
          statusArg(status),
          ...perfTailArgs,
          shiftArg(shifts),
        ]
      : fillerNull,
    { keepPreviousData: true, keyExtra: scopeKeyExtra(items[2]?.filter) },
  ));
  const perf3 = useLocalizedCompare(useMethod<OpsCommandPerformancePayload>(
    'opsCommand/getPerformance',
    perfEnabled(3)
      ? [
          restaurantId,
          ...perfHeadArgs,
          scopeArg(items[3]!.filter),
          sourceArg(source),
          statusArg(status),
          ...perfTailArgs,
          shiftArg(shifts),
        ]
      : fillerNull,
    { keepPreviousData: true, keyExtra: scopeKeyExtra(items[3]?.filter) },
  ));
  const perf4 = useLocalizedCompare(useMethod<OpsCommandPerformancePayload>(
    'opsCommand/getPerformance',
    perfEnabled(4)
      ? [
          restaurantId,
          ...perfHeadArgs,
          scopeArg(items[4]!.filter),
          sourceArg(source),
          statusArg(status),
          ...perfTailArgs,
          shiftArg(shifts),
        ]
      : fillerNull,
    { keepPreviousData: true, keyExtra: scopeKeyExtra(items[4]?.filter) },
  ));

  const ns0 = useMethod<NorthStarPayload | null>(
    'opsCommand/getNorthStar',
    northStarEnabled(0) ? [...northStarArgs(items[0]!.filter)] : fillerNull,
    { keepPreviousData: true, keyExtra: scopeKeyExtra(items[0]?.filter) },
  );
  const ns1 = useMethod<NorthStarPayload | null>(
    'opsCommand/getNorthStar',
    northStarEnabled(1) ? [...northStarArgs(items[1]!.filter)] : fillerNull,
    { keepPreviousData: true, keyExtra: scopeKeyExtra(items[1]?.filter) },
  );
  const ns2 = useMethod<NorthStarPayload | null>(
    'opsCommand/getNorthStar',
    northStarEnabled(2) ? [...northStarArgs(items[2]!.filter)] : fillerNull,
    { keepPreviousData: true, keyExtra: scopeKeyExtra(items[2]?.filter) },
  );
  const ns3 = useMethod<NorthStarPayload | null>(
    'opsCommand/getNorthStar',
    northStarEnabled(3) ? [...northStarArgs(items[3]!.filter)] : fillerNull,
    { keepPreviousData: true, keyExtra: scopeKeyExtra(items[3]?.filter) },
  );
  const ns4 = useMethod<NorthStarPayload | null>(
    'opsCommand/getNorthStar',
    northStarEnabled(4) ? [...northStarArgs(items[4]!.filter)] : fillerNull,
    { keepPreviousData: true, keyExtra: scopeKeyExtra(items[4]?.filter) },
  );

  // Fixed 5-slot settings audits — Settings matrix already shows multi-outlet
  // rows, so we fetch each compared scope and merge into one payload.
  const auditPoll = { refreshInterval: 300_000 } as const;
  function settingsAuditArgs(filter: string) {
    return [
      restaurantId,
      scopeArg(filter),
      perfSettings.aggregation,
      perfSettings.frameCount,
      perfSettings.frameUnit,
      perfSettings.dateStart ?? null,
      perfSettings.dateEnd ?? null,
    ] as const;
  }

  const audit0 = useMethod<SettingsAuditPayload>(
    'opsCommand/getSettingsAudit',
    settingsEnabled(0) ? [...settingsAuditArgs(items[0]!.filter)] : fillerNull,
    auditPoll,
  );
  const audit1 = useMethod<SettingsAuditPayload>(
    'opsCommand/getSettingsAudit',
    settingsEnabled(1) ? [...settingsAuditArgs(items[1]!.filter)] : fillerNull,
    auditPoll,
  );
  const audit2 = useMethod<SettingsAuditPayload>(
    'opsCommand/getSettingsAudit',
    settingsEnabled(2) ? [...settingsAuditArgs(items[2]!.filter)] : fillerNull,
    auditPoll,
  );
  const audit3 = useMethod<SettingsAuditPayload>(
    'opsCommand/getSettingsAudit',
    settingsEnabled(3) ? [...settingsAuditArgs(items[3]!.filter)] : fillerNull,
    auditPoll,
  );
  const audit4 = useMethod<SettingsAuditPayload>(
    'opsCommand/getSettingsAudit',
    settingsEnabled(4) ? [...settingsAuditArgs(items[4]!.filter)] : fillerNull,
    auditPoll,
  );

  const allLive = [live0, live1, live2, live3, live4];
  const allPerf = [perf0, perf1, perf2, perf3, perf4];
  const allNs = [ns0, ns1, ns2, ns3, ns4];
  const allAudits = [audit0, audit1, audit2, audit3, audit4];
  const currentPerf = allPerf.map(result =>
    matchesPerfSettings(result.data) ? result.data : null,
  );
  const settingsAudit =
    mode === 'settings'
      ? mergeSettingsAudits(items.map((_, i) => allAudits[i]?.data))
      : null;

  return {
    enabled,
    items: items.map((item, i) => ({
      ...item,
      live: allLive[i]?.data ?? null,
      perf: currentPerf[i],
      northStar: allNs[i]?.data ?? null,
      dataSource: overlay?.connected.includes('opentable')
        ? overlay.scopeGroups.find(
            g => g.id === scopeGroupIdFromFilter(item.filter),
          )?.dataSource
        : undefined,
    })),
    settingsAudit,
    loading:
      mode === 'live'
        ? items.some((_, i) => allLive[i].isLoading)
        : mode === 'settings'
          ? items.some(
              (_, i) =>
                !allAudits[i]?.data &&
                (allAudits[i]?.isLoading || allAudits[i]?.isValidating),
            )
          : items.some(
              (_, i) =>
                !currentPerf[i] &&
                (allPerf[i].isLoading || allPerf[i].isValidating),
            ) ||
            (hasNorthStar &&
              items.some(
                (_, i) =>
                  perfEnabled(i) &&
                  !allNs[i]?.data &&
                  (allNs[i]?.isLoading || allNs[i]?.isValidating),
              )),
  };
}

export function useConnectedSources(
  serverConnected?: ConnectedSourceId[],
): ConnectedSourceId[] {
  const [connected, setConnected] = useState<ConnectedSourceId[]>(
    serverConnected ?? ['reservations', 'tableplan'],
  );

  useEffect(() => {
    if (serverConnected) {
      setConnected(serverConnected);
    }
  }, [serverConnected?.join(',')]);

  return connected;
}
